import { useState, useEffect } from "react";
import axios from "axios";
import { X } from "lucide-react";
import ModalPortal from "@/components/ModalPortal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DISPLAY_NAMES = { Koebenhavn: "København" };
const displayName = (n) => DISPLAY_NAMES[n] || n;

function scoreColor(score) {
  if (score == null) return "#52525B";
  if (score >= 90) return "#22C55E";
  if (score >= 75) return "#84CC16";
  if (score >= 50) return "#EAB308";
  if (score >= 25) return "#F97316";
  return "#EF4444";
}

const fmtScore = (s) => (s != null ? Math.round(s) : "—");

// --- Detalje-panel: alle byer sorteret efter score (#47.7) ---
function TopscorerModal({ data, onClose }) {
  const byer = data?.byer || [];
  return (
    <ModalPortal onClose={onClose}>
      <div className="bg-card border border-border rounded-2xl shadow-xl w-full max-w-lg p-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-base font-bold text-foreground flex items-center gap-2">
              <span aria-hidden>🏆</span> Live Topscorer
            </h3>
            <p className="text-[13px] text-muted-foreground mt-0.5">
              Hvilken skole performer bedst lige nu
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors p-1 -mr-1"
            aria-label="Luk"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Forklaring + komponentvægte */}
        <div className="rounded-xl bg-secondary/50 px-4 py-3 mb-4 text-[13px] text-muted-foreground leading-snug">
          <p className="mb-2">
            Scoren er et vægtet gennemsnit pr. åbnings-time, så lukkedage ikke tæller imod.
          </p>
          <p className="font-semibold text-foreground mb-1">Komponenter:</p>
          <ul className="space-y-0.5">
            <li className="flex justify-between"><span>· Gns. kr. pr. åbnings-time</span><span className="tabular-nums">vægt 30%</span></li>
            <li className="flex justify-between"><span>· Booking-% af åbne timer</span><span className="tabular-nums">vægt 25%</span></li>
            <li className="flex justify-between"><span>· Fremmøde-% af planlagte vagter</span><span className="tabular-nums">vægt 15%</span></li>
            <li className="flex justify-between"><span>· Dagens mål opfyldt-%</span><span className="tabular-nums">vægt 30%</span></li>
          </ul>
          <p className="mt-2 italic">Elevtilfredshed indgår når den måles dagligt.</p>
        </div>

        <div className="space-y-2">
          {byer.map((b) => {
            const color = scoreColor(b.score);
            const noData = b.score == null;
            const detalje = [
              b.kr_pr_time != null ? `${b.kr_pr_time} kr/t` : null,
              b.booking_pct != null ? `${b.booking_pct}% booking` : null,
              b.fremmoede_pct != null ? `${b.fremmoede_pct}% fremmøde` : null,
              b.dagens_maal_pct != null ? `${b.dagens_maal_pct}% af dagens mål` : null,
            ].filter(Boolean).join(" · ");
            return (
              <div
                key={b.by}
                className="flex items-center gap-3 rounded-xl border border-border bg-background/40 px-3 py-2.5"
              >
                <span
                  className="w-6 h-6 shrink-0 rounded-full flex items-center justify-center text-[12px] font-bold"
                  style={{ background: `${color}22`, color }}
                >
                  {b.rang}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-sm font-semibold text-foreground truncate">
                      {displayName(b.by)}
                    </span>
                    {noData ? (
                      <span className="text-[12px] text-muted-foreground">ingen data</span>
                    ) : (
                      <span className="text-sm font-bold font-kpi" style={{ color }}>
                        Score {fmtScore(b.score)}
                      </span>
                    )}
                  </div>
                  {!noData && detalje && (
                    <div className="text-[12px] text-muted-foreground mt-0.5">{detalje}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </ModalPortal>
  );
}

// --- Kompakt topscorer-boks (top-højre) ---
export function LiveTopscorer({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/dashboard/topscorer`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [token]);

  // Fejl eller endnu ingen topscorer → diskret eller intet (må ikke crashe siden)
  if (error || !data) return null;

  const top = data.topscorer;
  if (!top) {
    return (
      <span className="text-[12px] text-muted-foreground">Topscorer afventer data</span>
    );
  }

  const color = scoreColor(top.score);
  return (
    <>
      {/* Samme højde som menu-pillerne: h-10 (mobil) / md:h-12 (desktop). */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-left h-10 md:h-12 rounded-full border border-border bg-card pl-3 pr-4 md:pr-5 shadow-sm hover:shadow-md hover:border-[#FF2A6D]/50 transition-all flex items-center gap-2.5"
        title="Live Topscorer — performance seneste 7 dage. Klik for alle byer."
      >
        <span className="text-xl md:text-2xl leading-none" aria-hidden>🏆</span>
        <div className="min-w-0 leading-tight">
          <div className="text-[10px] md:text-[11px] font-bold uppercase tracking-wider text-[#FF2A6D] leading-none">
            Live Topscorer
          </div>
          <div className="flex items-baseline gap-1.5 mt-0.5">
            <span className="text-[13px] md:text-base font-bold text-foreground truncate">
              {displayName(top.by)}
            </span>
            <span className="text-[13px] md:text-base font-bold font-kpi" style={{ color }}>
              {fmtScore(top.score)}
            </span>
          </div>
        </div>
      </button>

      {open && <TopscorerModal data={data} onClose={() => setOpen(false)} />}
    </>
  );
}

export default LiveTopscorer;
